/**
 * `modifier.ts` — **modifier / condiment**: pilihan yang menempel pada satu baris pesanan.
 *
 * ### ⭐ Ini BUKAN combo, dan menyatukannya akan merusak pajak
 * | | Combo (set menu) | Modifier |
 * |---|---|---|
 * | Harga | induk memegang, komponen **NOL** | modifier **MENAMBAH** harga baris induknya |
 * | Pajak | hanya induk | ikut, karena ia menambah dasar pengenaan |
 * | Bentuk | produk yang menjual produk lain | atribut sebuah baris |
 *
 * ⛔ Komponen combo **dipaksa berharga nol** oleh CHECK di database. Menumpangkan modifier
 * berbayar ke sana akan langsung menabrak constraint — dan kalau constraint-nya dilonggarkan agar
 * "muat", pajak combo ikut rusak.
 *
 * ### ⭐ Gradasi BERTIGA, dibedakan sejak awal (bukti VTEC, teardown §8d)
 * `Modifier` (gratis) → `Modifier with price` (berbayar) → `Modifier with price: Reduce Stock`
 * (berbayar **dan** memotong stok). Yang ketiga menjawab kasus *"extra telur"* yang benar-benar
 * mengurangi persediaan. Menyatukannya dengan yang kedua membuat stok bocor diam-diam.
 *
 * ### ⚠️ Seberapa sering dipakai — dan kenapa ini dikerjakan sebelum banyak hal lain
 * Sampel `raw_pos_orderdetail` Arena: baris ber-`iscomment = 1` (modifier & komentar) berjumlah
 * **5.633.478**, sementara baris combo (`productsettype = 7`) **566.545**. Modifier dipakai
 * kira-kira **sepuluh kali lebih sering** daripada set menu.
 *
 * @module
 */
/**
 * Jenis modifier. ⚠️ **Enum, bukan dua boolean** (`is_priced`, `reduces_stock`): dua boolean
 * mengizinkan kombinasi yang tak bermakna (memotong stok tapi gratis) dan menyembunyikan bahwa
 * ketiganya sebenarnya **tangga**, bukan sifat yang saling lepas.
 */
export const MODIFIER_KINDS = Object.freeze([
    /** *less ice*, *tanpa sambal* — mengubah cara membuat, bukan harga. */
    'free',
    /** *extra shot* — menambah harga baris induknya. */
    'priced',
    /** *extra telur* — menambah harga **dan** benar-benar mengurangi persediaan. */
    'priced_stock',
]);
const KIND_SET = new Set(MODIFIER_KINDS);
/** Guard {@link ModifierKind}. */
export function isModifierKind(v) {
    return typeof v === 'string' && KIND_SET.has(v);
}
/** Apakah jenis ini menambah harga baris? */
export function modifierAddsPrice(kind) {
    return kind !== 'free';
}
/**
 * Apakah jenis ini memotong persediaan?
 *
 * ⚠️ **Pemotongan stoknya BELUM diimplementasikan** — modul stok belum ada. Kolomnya sudah
 * dibedakan sejak awal justru supaya saat stok tiba, tak ada yang perlu menebak modifier mana yang
 * seharusnya memotong: menebaknya belakangan berarti menebak untuk data yang sudah terlanjur
 * tercatat.
 */
export function modifierReducesStock(kind) {
    return kind === 'priced_stock';
}
/** Panjang maksimum teks bebas. Cukup untuk instruksi, terlalu pendek untuk jadi catatan resep. */
export const MODIFIER_FREE_TEXT_MAX = 120;
/**
 * ⭐ Kontrol yang MEMAKSA (P6) — dan seperti `validateComboSelections`, ia **tak pernah melempar**
 * supaya layar bisa menampilkan semua pelanggaran sekaligus, bukan satu per satu ketukan.
 */
export function validateModifierSelections(rules, selections) {
    const out = [];
    const perGrup = new Map();
    for (const s of selections) {
        const d = perGrup.get(s.groupCode);
        if (d === undefined)
            perGrup.set(s.groupCode, [s]);
        else
            d.push(s);
    }
    const ruleCode = new Set(rules.map((r) => r.groupCode));
    for (const code of perGrup.keys()) {
        if (!ruleCode.has(code)) {
            out.push({
                groupCode: code,
                reason: 'unknown_group',
                detail: `grup modifier "${code}" tak berlaku untuk produk ini`,
            });
        }
    }
    for (const r of rules) {
        const selected = perGrup.get(r.groupCode) ?? [];
        for (const s of selected) {
            const hasText = (s.freeText ?? '').trim() !== '';
            const hasOption = (s.optionCode ?? '') !== '';
            if (!hasText && !hasOption) {
                out.push({
                    groupCode: r.groupCode,
                    reason: 'empty',
                    detail: `pilihan kosong di grup "${r.groupCode}" — tak ada opsi maupun teks`,
                });
                continue;
            }
            if (hasText && !r.allowFreeText) {
                // ⛔ Grup yang tak mengizinkan teks bebas biasanya punya alasan: dapur tak membacanya.
                out.push({
                    groupCode: r.groupCode,
                    reason: 'free_text_denied',
                    detail: `grup "${r.groupCode}" tak menerima teks bebas`,
                });
            }
            if (hasOption && !r.options.includes(s.optionCode)) {
                out.push({
                    groupCode: r.groupCode,
                    reason: 'not_allowed',
                    detail: `"${String(s.optionCode)}" bukan opsi di grup "${r.groupCode}"`,
                });
            }
        }
        if (selected.length < r.minSelect) {
            out.push({
                groupCode: r.groupCode,
                reason: 'too_few',
                detail: `grup "${r.groupCode}" butuh minimal ${String(r.minSelect)}, terpilih ${String(selected.length)}`,
            });
        }
        if (r.maxSelect > 0 && selected.length > r.maxSelect) {
            out.push({
                groupCode: r.groupCode,
                reason: 'too_many',
                detail: `grup "${r.groupCode}" maksimal ${String(r.maxSelect)}, terpilih ${String(selected.length)}`,
            });
        }
    }
    return out;
}
//# sourceMappingURL=modifier.js.map